// --- React
import React, { useCallback } from "react";
// --- UI Components
import Toast from "@dtci/spellbook/Toast";
import {
  ToastPopout,
  ToastContainer,
  ReactToast,
} from "@dtci/spellbook/ToastPopout";
import Typography from "@dtci/spellbook/Typography";
// --- Styles
import styles from "./toast-notifications.module.scss";

const TOAST_ID = "toast-notifications-container";
const dismissedJobProgressToasts = new Set();

const PROGRESS_TOAST_OPTIONS = {
  autoClose: false,
  closeOnClick: false,
  closeButton: true,
  hideProgressBar: true,
};

/**
 * Stable toast id for a background job progress notification.
 * @param {string|number} jobId
 * @returns {string}
 */
export function getJobProgressToastId(jobId) {
  return `job-progress-${jobId}`;
}

/**
 * @param {string} type
 * @returns {string}
 */
function getToastIcon(type) {
  if (type === "success") return "fa-check-circle";
  if (type === "warning") return "fa-triangle-exclamation";
  return "fa-circle-exclamation";
}

/**
 * @param {string} message
 * @param {string} type
 * @param {Object} [options]
 * @param {boolean} [options.persistent=false]
 * @param {string|number} [options.toastId]
 * @returns {JSX.Element}
 */
function buildToastContent(message, type, options = {}) {
  const { persistent = false, toastId } = options;
  const handleClose =
    persistent && toastId != null
      ? () => {
          dismissedJobProgressToasts.add(String(toastId));
          ReactToast.dismiss(toastId);
        }
      : undefined;

  return (
    <Toast
      className={`${styles.container} ${persistent ? styles.persistent : ""}`}
      type={type}
      hideClose={!persistent}
      onClose={handleClose}
      iconTypePrimary={`fa-regular fa-beat fa-lg ${getToastIcon(type)}`}
      theme="light"
    >
      <Typography variant="T30">{message}</Typography>
    </Toast>
  );
}

/**
 * Custom hook to display toast notifications.
 *
 * @returns {Object} Toast helpers including `openToast` and job progress handlers.
 */
export const useToastNotification = () => {
  const openToast = useCallback((message, type = "info") => {
    return ToastPopout({
      position: "top-right",
      closeOnClick: true,
      animate: true,
      containerId: TOAST_ID,
      content: buildToastContent(message, type),
    });
  }, []);

  const openJobProgressToast = useCallback((jobId, message, type = "info") => {
    const toastId = getJobProgressToastId(jobId);

    if (dismissedJobProgressToasts.has(toastId)) {
      return;
    }

    const content = buildToastContent(message, type, {
      persistent: true,
      toastId,
    });

    if (ReactToast.isActive(toastId)) {
      ReactToast.update(toastId, {
        render: content,
        ...PROGRESS_TOAST_OPTIONS,
      });
      return;
    }

    ToastPopout({
      toastId,
      position: "top-right",
      animate: true,
      containerId: TOAST_ID,
      content,
      ...PROGRESS_TOAST_OPTIONS,
    });
  }, []);

  const createJobProgressToastHandler = useCallback(
    (jobId) => {
      dismissedJobProgressToasts.delete(getJobProgressToastId(jobId));

      return (message, type = "info") => {
        openJobProgressToast(jobId, message, type);
      };
    },
    [openJobProgressToast],
  );

  return { openToast, openJobProgressToast, createJobProgressToastHandler };
};

/**
 * ToastNotificationProvider component that initializes a toast notification container.
 *
 * @returns {JSX.Element} The rendered ToastContainer component.
 */
const ToastNotificationProvider = () => (
  <ToastContainer
    containerId={TOAST_ID}
    enableMultiContainer
    inline={false}
    autoClose={5000}
  />
);

export default ToastNotificationProvider;
